import type { FC, SyntheticEvent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Box,
  Checkbox,
  Chip,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import { OrderByControl, showToast } from '@/common/components'
import type { SortOption } from '@/common/components'
import { useApiKeyListQuery, useRevokeApiKeys } from '../api'
import type { ApiKeyListItem, ApiKeySortOrder, ApiKeyStatus } from '../api'
import {
  BulkActionBar,
  BulkConfirmDialog,
  cellSx,
  chipSx,
  formatDate,
  headerCellSx,
  InlineConfirmRow,
  PaginatedListModal,
  rowSx,
  useListSelection,
  visuallyHiddenStyle,
} from './shared'
import type { BulkConfirmItem } from './shared'

interface ApiKeysListModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

export const ApiKeysListModal: FC<ApiKeysListModalProps> = ({ open, onClose }) => {
  const [page, setPage] = useState(0)
  const [activeTab, setActiveTab] = useState<ApiKeyStatus>('active')
  const [sortOrder, setSortOrder] = useState<ApiKeySortOrder>('newest_first')
  const [selectedEnvironments, setSelectedEnvironments] = useState<readonly Environment[]>([])
  const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState(false)
  const {
    selectedIds,
    confirmingId,
    setSelectedIds,
    setConfirmingId,
    handleSelectOne,
    handleRequestConfirm,
    handleCancelConfirm,
    resetSelection,
  } = useListSelection()

  const isActiveTab = activeTab === 'active'

  const environmentFilter =
    isActiveTab && selectedEnvironments.length > 0 ? selectedEnvironments : undefined
  const sortFilter = isActiveTab ? sortOrder : undefined

  const { data, isLoading, isError, error, refetch } = useApiKeyListQuery({
    page: 0,
    size: MAX_FETCH_SIZE,
    enabled: open,
    status: activeTab,
    ...(environmentFilter ? { environment: environmentFilter } : {}),
    ...(sortFilter ? { sort: sortFilter } : {}),
  })

  const { revokeAsync, isRevoking, reset: resetRevoke } = useRevokeApiKeys()

  const allContent = useMemo(() => data?.content ?? [], [data?.content])

  const content = useMemo(() => {
    // Filter by tab (status) — backend currently ignores status param
    let filtered = allContent.filter((key) => {
      if (activeTab === 'active') {
        return key.isActive && !key.isExpired
      }
      return !key.isActive || key.isExpired
    })

    // Filter by environment (Active tab only) — backend ignores environment param
    if (isActiveTab && selectedEnvironments.length > 0) {
      filtered = filtered.filter((key) =>
        selectedEnvironments.includes(key.environment as Environment),
      )
    }

    // Sort — backend ignores sort param
    if (isActiveTab) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortOrder) {
          case 'newest_first':
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          case 'name_asc':
            return (a.name ?? '').localeCompare(b.name ?? '')
        }
      })
    } else {
      // Revoked tab: fixed sort by createdAt DESC
      filtered = [...filtered].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    }

    return filtered
  }, [allContent, activeTab, isActiveTab, selectedEnvironments, sortOrder])

  const paginatedContent = useMemo(() => {
    const maxPage = Math.max(0, Math.ceil(content.length / PAGE_SIZE) - 1)
    const safePage = Math.min(page, maxPage)
    return content.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)
  }, [content, page])

  const revocableIds = useMemo(
    () =>
      new Set(
        paginatedContent.filter((key) => key.isActive && !key.isExpired).map((key) => key.keyId),
      ),
    [paginatedContent],
  )

  const allRevocableSelected =
    revocableIds.size > 0 && [...revocableIds].every((id) => selectedIds.has(id))
  const someRevocableSelected =
    !allRevocableSelected && [...revocableIds].some((id) => selectedIds.has(id))

  const handleClose = useCallback(() => {
    setPage(0)
    setActiveTab('active')
    setSortOrder('newest_first')
    setSelectedEnvironments([])
    resetSelection()
    setIsBulkConfirmOpen(false)
    resetRevoke()
    onClose()
  }, [onClose, resetRevoke, resetSelection])

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, newTab: ApiKeyStatus) => {
      setActiveTab(newTab)
      setPage(0)
      setSortOrder('newest_first')
      setSelectedEnvironments([])
      resetSelection()
    },
    [resetSelection],
  )

  const handleSortChange = useCallback(
    (newSort: ApiKeySortOrder) => {
      setSortOrder(newSort)
      setPage(0)
      resetSelection()
    },
    [resetSelection],
  )

  const handleEnvironmentToggle = useCallback(
    (env: Environment) => {
      setSelectedEnvironments((prev) => {
        const isSelected = prev.includes(env)
        return isSelected ? prev.filter((e) => e !== env) : [...prev, env]
      })
      setPage(0)
      resetSelection()
    },
    [resetSelection],
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage)
      resetSelection()
    },
    [resetSelection],
  )

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = [...revocableIds].every((id) => prev.has(id))
      return allSelected ? new Set<string>() : new Set(revocableIds)
    })
  }, [revocableIds, setSelectedIds])

  const handleRevoke = useCallback(
    (keyId: string) => {
      void (async () => {
        try {
          const result = await revokeAsync({ keyIds: [keyId] })
          if (result.success) {
            showToast('API key revoked', 'success')
            setConfirmingId(null)
            setSelectedIds((prev) => {
              const next = new Set(prev)
              next.delete(keyId)
              return next
            })
            if (paginatedContent.length === 1 && page > 0) {
              setPage((prev) => prev - 1)
            }
          } else {
            showToast(result.message, 'error')
          }
        } catch (err: unknown) {
          showToast(err instanceof Error ? err.message : 'Failed to revoke API key', 'error')
        }
      })().catch(() => {
        // Safety net: should never reach here if try/catch is comprehensive
      })
    },
    [revokeAsync, paginatedContent.length, page, setConfirmingId, setSelectedIds],
  )

  const bulkConfirmItems: readonly BulkConfirmItem[] = useMemo(
    () =>
      paginatedContent
        .filter((key) => selectedIds.has(key.keyId))
        .map((key) => ({
          id: key.keyId,
          displayName: getDisplayName(key),
        })),
    [paginatedContent, selectedIds],
  )

  const handleBulkRevoke = useCallback(() => {
    void (async () => {
      try {
        const keyIds = bulkConfirmItems.map((item) => item.id)
        const result = await revokeAsync({ keyIds })
        if (result.success) {
          showToast(`Revoked ${keyIds.length} API keys`, 'success')
          resetSelection()
          setIsBulkConfirmOpen(false)
          const remainingAfterRevoke = paginatedContent.length - keyIds.length
          if (remainingAfterRevoke === 0 && page > 0) {
            setPage((prev) => prev - 1)
          }
        } else {
          showToast(result.message, 'error')
        }
      } catch (err: unknown) {
        showToast(err instanceof Error ? err.message : 'Failed to revoke API keys', 'error')
      }
    })().catch(() => {
      // Safety net: should never reach here if try/catch is comprehensive
    })
  }, [bulkConfirmItems, revokeAsync, paginatedContent.length, page, resetSelection])

  const handleOpenBulkConfirm = useCallback(() => {
    setIsBulkConfirmOpen(true)
  }, [])

  const handleCloseBulkConfirm = useCallback(() => {
    setIsBulkConfirmOpen(false)
  }, [])

  const toolbar = (
    <Box>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="API key status filter"
        sx={tabsSx}
      >
        <Tab label="Active" value="active" />
        <Tab label="Revoked & Expired" value="revoked_expired" />
      </Tabs>

      <Typography
        component="span"
        style={visuallyHiddenStyle}
        aria-live="polite"
        aria-atomic="true"
      >
        Showing {activeTab === 'active' ? 'active' : 'revoked and expired'} API keys
      </Typography>

      {isActiveTab && (
        <Box sx={filterRowSx}>
          <Box sx={environmentChipsSx} role="group" aria-label="Filter by environment">
            {ENVIRONMENT_OPTIONS.map((env) => {
              const isSelected = selectedEnvironments.includes(env)
              return (
                <Chip
                  key={env}
                  label={env}
                  onClick={() => handleEnvironmentToggle(env)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  size="small"
                  aria-pressed={isSelected}
                  sx={environmentChipSx}
                />
              )
            })}
          </Box>
          <OrderByControl<ApiKeySortOrder>
            options={API_KEY_SORT_OPTIONS}
            value={sortOrder}
            onChange={handleSortChange}
            ariaLabel="API keys sort order"
          />
        </Box>
      )}
    </Box>
  )

  return (
    <>
      <PaginatedListModal
        open={open}
        onClose={handleClose}
        title="API Keys"
        totalElements={content.length}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={!isLoading && content.length === 0}
        emptyMessage="No API keys found."
        onRetry={refetch}
        toolbar={toolbar}
        bulkActionBar={
          isActiveTab ? (
            <BulkActionBar
              selectedCount={selectedIds.size}
              actionLabel="Revoke"
              onAction={handleOpenBulkConfirm}
              isActioning={isRevoking}
            />
          ) : undefined
        }
      >
        {isActiveTab ? (
          <ActiveKeysTable
            content={paginatedContent}
            confirmingId={confirmingId}
            isRevoking={isRevoking}
            selectedIds={selectedIds}
            revocableIds={revocableIds}
            allRevocableSelected={allRevocableSelected}
            someRevocableSelected={someRevocableSelected}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onRequestConfirm={handleRequestConfirm}
            onConfirm={handleRevoke}
            onCancel={handleCancelConfirm}
          />
        ) : (
          <RevokedExpiredKeysTable content={paginatedContent} />
        )}
      </PaginatedListModal>

      <BulkConfirmDialog
        open={isBulkConfirmOpen}
        onClose={handleCloseBulkConfirm}
        onConfirm={handleBulkRevoke}
        isActioning={isRevoking}
        actionLabel="Revoke"
        entityLabel="API Key"
        entityLabelPlural="API Keys"
        items={bulkConfirmItems}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Private: Active keys table
// ---------------------------------------------------------------------------

interface ActiveKeysTableProps {
  readonly content: readonly ApiKeyListItem[]
  readonly confirmingId: string | null
  readonly isRevoking: boolean
  readonly selectedIds: ReadonlySet<string>
  readonly revocableIds: ReadonlySet<string>
  readonly allRevocableSelected: boolean
  readonly someRevocableSelected: boolean
  readonly onSelectAll: () => void
  readonly onSelectOne: (id: string) => void
  readonly onRequestConfirm: (id: string) => void
  readonly onConfirm: (id: string) => void
  readonly onCancel: () => void
}

const ActiveKeysTable: FC<ActiveKeysTableProps> = ({
  content,
  confirmingId,
  isRevoking,
  selectedIds,
  revocableIds,
  allRevocableSelected,
  someRevocableSelected,
  onSelectAll,
  onSelectOne,
  onRequestConfirm,
  onConfirm,
  onCancel,
}) => (
  <Table size="small" aria-label="Active API keys">
    <caption style={visuallyHiddenStyle}>
      List of active API keys with name, environment, status, and actions
    </caption>
    <TableHead>
      <TableRow>
        <TableCell sx={headerCellSx} padding="checkbox">
          <Checkbox
            checked={allRevocableSelected}
            indeterminate={someRevocableSelected}
            onChange={onSelectAll}
            disabled={revocableIds.size === 0}
            inputProps={{ 'aria-label': 'Select all items on this page' }}
            size="small"
          />
        </TableCell>
        <TableCell sx={headerCellSx}>Name</TableCell>
        <TableCell sx={headerCellSx}>Environment</TableCell>
        <TableCell sx={headerCellSx}>Status</TableCell>
        <TableCell sx={headerCellSx}>Created</TableCell>
        <TableCell sx={headerCellSx}>Expires</TableCell>
        <TableCell sx={headerCellSx}>Actions</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {content.map((key) => (
        <ActiveApiKeyRow
          key={key.keyId}
          item={key}
          confirmingId={confirmingId}
          onRequestConfirm={onRequestConfirm}
          onConfirm={onConfirm}
          onCancel={onCancel}
          isRevoking={isRevoking}
          isSelected={selectedIds.has(key.keyId)}
          onSelect={onSelectOne}
        />
      ))}
    </TableBody>
  </Table>
)

// ---------------------------------------------------------------------------
// Private: Revoked & expired keys table
// ---------------------------------------------------------------------------

interface RevokedExpiredKeysTableProps {
  readonly content: readonly ApiKeyListItem[]
}

const RevokedExpiredKeysTable: FC<RevokedExpiredKeysTableProps> = ({ content }) => (
  <Table size="small" aria-label="Revoked and expired API keys">
    <caption style={visuallyHiddenStyle}>
      List of revoked and expired API keys with name, environment, status, and dates
    </caption>
    <TableHead>
      <TableRow>
        <TableCell sx={headerCellSx}>Name</TableCell>
        <TableCell sx={headerCellSx}>Environment</TableCell>
        <TableCell sx={headerCellSx}>Status</TableCell>
        <TableCell sx={headerCellSx}>Created</TableCell>
        <TableCell sx={headerCellSx}>Expires</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {content.map((key) => (
        <TableRow key={key.keyId} sx={rowSx}>
          <TableCell sx={cellSx}>{getDisplayName(key)}</TableCell>
          <TableCell sx={cellSx}>{key.environment}</TableCell>
          <TableCell sx={cellSx}>
            <StatusChip isActive={key.isActive} isExpired={key.isExpired} />
          </TableCell>
          <TableCell sx={cellSx}>{formatDate(key.createdAt)}</TableCell>
          <TableCell sx={cellSx}>{formatDate(key.expiresAt)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

// ---------------------------------------------------------------------------
// Private: Active row component
// ---------------------------------------------------------------------------

interface ActiveApiKeyRowProps {
  readonly item: ApiKeyListItem
  readonly confirmingId: string | null
  readonly onRequestConfirm: (id: string) => void
  readonly onConfirm: (id: string) => void
  readonly onCancel: () => void
  readonly isRevoking: boolean
  readonly isSelected: boolean
  readonly onSelect: (id: string) => void
}

const ActiveApiKeyRow: FC<ActiveApiKeyRowProps> = ({
  item,
  confirmingId,
  onRequestConfirm,
  onConfirm,
  onCancel,
  isRevoking,
  isSelected,
  onSelect,
}) => {
  const isRevocable = item.isActive && !item.isExpired
  const displayName = getDisplayName(item)
  const confirmMessage = `Revoke API key "${displayName}"?`

  const handleSelect = (): void => {
    onSelect(item.keyId)
  }

  const checkboxCell = (
    <TableCell sx={cellSx} padding="checkbox">
      <Checkbox
        checked={isSelected}
        onChange={handleSelect}
        disabled={!isRevocable}
        inputProps={{ 'aria-label': `Select ${displayName}` }}
        size="small"
      />
    </TableCell>
  )

  if (!isRevocable) {
    return (
      <TableRow sx={rowSx}>
        {checkboxCell}
        <TableCell sx={cellSx}>{displayName}</TableCell>
        <TableCell sx={cellSx}>{item.environment}</TableCell>
        <TableCell sx={cellSx}>
          <StatusChip isActive={item.isActive} isExpired={item.isExpired} />
        </TableCell>
        <TableCell sx={cellSx}>{formatDate(item.createdAt)}</TableCell>
        <TableCell sx={cellSx}>{formatDate(item.expiresAt)}</TableCell>
        <TableCell sx={cellSx} />
      </TableRow>
    )
  }

  return (
    <InlineConfirmRow
      id={item.keyId}
      confirmingId={confirmingId}
      onRequestConfirm={onRequestConfirm}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isActioning={isRevoking}
      actionLabel="Revoke"
      confirmMessage={confirmMessage}
      columnCount={ACTIVE_COLUMN_COUNT}
    >
      {checkboxCell}
      <TableCell sx={cellSx}>{displayName}</TableCell>
      <TableCell sx={cellSx}>{item.environment}</TableCell>
      <TableCell sx={cellSx}>
        <StatusChip isActive={item.isActive} isExpired={item.isExpired} />
      </TableCell>
      <TableCell sx={cellSx}>{formatDate(item.createdAt)}</TableCell>
      <TableCell sx={cellSx}>{formatDate(item.expiresAt)}</TableCell>
    </InlineConfirmRow>
  )
}

// ---------------------------------------------------------------------------
// Private: Status chip
// ---------------------------------------------------------------------------

interface StatusChipProps {
  readonly isActive: boolean
  readonly isExpired: boolean
}

const StatusChip: FC<StatusChipProps> = ({ isActive, isExpired }) => {
  if (!isActive) return <Chip label="Revoked" color="default" variant="outlined" sx={chipSx} />
  if (isExpired) return <Chip label="Expired" color="warning" variant="outlined" sx={chipSx} />
  return <Chip label="Active" color="success" variant="outlined" sx={chipSx} />
}

// ---------------------------------------------------------------------------
// Private: Helpers
// ---------------------------------------------------------------------------

function getDisplayName(item: ApiKeyListItem): string {
  return item.name ?? `Unnamed ${item.environment} key`
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type Environment = 'dev' | 'test' | 'live'

const ENVIRONMENT_OPTIONS: readonly Environment[] = ['dev', 'test', 'live']

const API_KEY_SORT_OPTIONS: readonly SortOption<ApiKeySortOrder>[] = [
  { value: 'newest_first', label: 'Newest first' },
  { value: 'name_asc', label: 'Name A \u2192 Z' },
] as const

const PAGE_SIZE = 20
const MAX_FETCH_SIZE = 1000
const ACTIVE_COLUMN_COUNT = 7

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const tabsSx: SxProps<Theme> = {
  mb: 1.5,
  '& .MuiTabs-indicator': {
    height: 2,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    minHeight: 40,
    py: 0,
  },
}

const filterRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 1,
}

const environmentChipsSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.75,
}

const environmentChipSx: SxProps<Theme> = {
  textTransform: 'capitalize',
  fontSize: '0.75rem',
}
